import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type { IntakeProfile, IntakeProfileInput } from "../domain/IntakeProfile";
import { CenterIntakeProfileAPI, IntakeProfileAPI } from "../infrastructure/api/intakeProfile.api";

const INTAKE_PROFILE_KEY = "patient-intake-profile";
const CENTER_INTAKE_PROFILE_KEY = "center-patient-intake-profile";

export const useIntakeProfile = (patientId: string) => {
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [INTAKE_PROFILE_KEY, patientId],
        queryFn: async () => (await IntakeProfileAPI.get(patientId)).data,
        retry: false,
    });
    return { data, loading: isLoading, failed: isError, retry: () => void refetch() };
};

/** Every write answers with the whole profile, which replaces the cached one. */
function useProfileWrite<Args>(patientId: string, write: (args: Args) => Promise<IntakeProfile>) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: write,
        onSuccess: (profile) => queryClient.setQueryData([INTAKE_PROFILE_KEY, patientId], profile),
    });
}

export const useSaveIntakeProfile = (patientId: string) =>
    useProfileWrite(patientId, async (values: IntakeProfileInput) => (await IntakeProfileAPI.save(patientId, values)).data);

export const useAcceptIntakeSuggestion = (patientId: string) =>
    useProfileWrite(
        patientId,
        async ({ suggestionId, value }: { suggestionId: string; value?: string }) =>
            (await IntakeProfileAPI.accept(patientId, suggestionId, value)).data,
    );

export const useDismissIntakeSuggestion = (patientId: string) =>
    useProfileWrite(patientId, async (suggestionId: string) => (await IntakeProfileAPI.dismiss(patientId, suggestionId)).data);

/** One held patient's profile, for the centre's administrator; nothing is read until one is opened. */
export const useCenterIntakeProfile = (centerId: string | undefined, patientId: string | null | undefined) => {
    const enabled = Boolean(centerId && patientId);
    const { data, isLoading, isError, refetch } = useQuery({
        queryKey: [CENTER_INTAKE_PROFILE_KEY, centerId, patientId],
        queryFn: async () => (await CenterIntakeProfileAPI.get(centerId as string, patientId as string)).data,
        enabled,
        retry: false,
    });
    return { data, loading: enabled && isLoading, failed: isError, retry: () => void refetch() };
};

export const useSaveCenterIntakeProfile = (centerId: string | undefined, patientId: string | null | undefined) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (values: IntakeProfileInput) =>
            (await CenterIntakeProfileAPI.save(centerId as string, patientId as string, values)).data,
        onSuccess: (saved) => {
            queryClient.setQueryData([CENTER_INTAKE_PROFILE_KEY, centerId, patientId], saved);
            queryClient.invalidateQueries({ queryKey: [INTAKE_PROFILE_KEY, patientId] });
        },
    });
};
